import { Box, StyleProps, Tab, TabIndicator, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { CorporationDto, UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { ConsumptionsStructure, UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $count, $email, $isemail, $length, $ok, $UUID } from "foundation-ts/commons";
import { $password } from "foundation-ts/crypto";
import { TSError } from "foundation-ts/tserrors";
import { Resp } from "foundation-ts/tsrequest";
import { Nullable, UUID } from "foundation-ts/types";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { ConsumptionsBox } from "@components/ConsumptionsBox/ConsumptionsBox";
import { AsyncLinkUserModal } from "@components/Modals/AsyncLinkUserModal";
import { AsyncConfirmModal, DefaultAsyncConfirmModalState } from "@components/Modals/AsyncConfirmModal";
import { WaitingModal } from "@components/Modals/WaitingModal";
import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { UserInfos, UserParameters } from "@components/UserInfos/UserInfos";
import { CorporationUserInfos } from "@components/UserInfos/CorporationUserInfos";
import { SearchUsers } from "@components/admin/SearchUsers";
import { config } from "@/config/config";
import { useAuthContext } from "@hooks/useAuthContext";
import useCreateUser from "@hooks/useCreateUser";
import useCountries from "@hooks/useCountries";
import useCorporations from "@hooks/useCorporations";
import usePatchUser from "@hooks/usePatchUser";
import { logout } from "@services/authentication.service";
import { createAdminCorporate, createCorporationUser, getCorporationUsers, linkUserToCorporation } from "@services/corporations.service";
import { exportConsumptions, getConsumptions, getCurrentConsumption } from "@services/stats.service";
import { createAuthentication, deleteUser, getUserProfile, searchUsersFromParams, setAdminRole } from "@services/users.service";
import { $g1Color } from "@/theme/G1Style";
import { defaultErrorToast, defaultInfoToast, defaultSuccessToast, defaultWarningToast } from "@utils/toast";
import { ActionButton } from "@utils/TypesAndConstants";

export const UsersView = ({ maxWidth }: StyleProps) => {
    const { connectedUser, disconnect } = useAuthContext();
    const { mutateAsync: createUser } = useCreateUser() ;
    const { mutateAsync: patchUser} = usePatchUser();
    const { data: corporations } = useCorporations() ;
    const { data: countries } = useCountries() ;
    const { isOpen, onOpen, onClose } = useDisclosure() ;
    const [visibleUserInfos, setVisibleUserInfos] = useState(false);
    const [actionButtons, setActionButtons] = useState<ActionButton<UserParameters>[]>([]);
    const [nbQualified, setNbQualified] = useState<number>(0);
    const [nbAdvanced, setNbAdvanced] = useState<number>(0);
    const [consumptions, setConsumptions] = useState<Nullable<ConsumptionsStructure>>(null);
    const [users, setUsers] = useState<UserParameters[]>([]) ;
    const [workedCorporateUsers, setWorkedCorporateUsers] = useState<UserParameters[]>([]);
    const [forceReload, setForceReload] = useState(0);
    const [isCorporateUserToShow, setIsCorporateUserToShow] = useState(false);
    const [isCreation, setIsCreation] = useState(true);
    const [isConnectedAdminCorporate, setIsConnectedAdminCorporate] = useState(false);
    const [isAddedAdminCorporate, setIsAddedAdminCorporate] = useState(false);
    const [isOpenLinkUserModal, setIsOpenLinkUserModal] = useState(false);
    const [confirmModal, setConfirmModal] = useState(DefaultAsyncConfirmModalState);
    const [usernameToLink, setUsernameToLink] = useState("");
    const [userIdToLink, setUserIdToLink] = useState<Nullable<UUID>>(null);
    const [availableCorpToLink, setAvailableCorpToLink] = useState<Nullable<CorporationDto[]>>(null);
    const userToShowRef = useRef<UserParameters|null>(null);
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search) ;

    const onSearchUser = async (search?: Nullable<string>) => {
        userToShowRef.current = null;
        setVisibleUserInfos(false) ;
        const users: UserParameters[] = [] ;
        try {
            if ($ok(search)) {
                const userIds = await searchUsersFromParams(search!, true) ;
                if (!$ok(userIds) || (userIds!.length === 0)) {
                    toast(defaultInfoToast(`Aucun ${isConnectedAdminCorporate ? 'collaborateur' : 'utilisateur'} trouvé avec ${$isemail(search) ? "l'email" : "le nom"} "${search}"`)) ;
                } else {
                    for (const userId of userIds!) {
                        users.push(await _toUserParameters(userId)) ;
                    }
                    setUsers(users) ;
                }
            } else {
                setUsers(users) ;
            }
        } catch (e) {
            if ((e as Error).message === Resp.Unauthorized.toString()) {
                toast(defaultErrorToast(`Erreur lors de la recherche ${isConnectedAdminCorporate ? "de collaborateurs" : "d'utilisateurs"}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`)) ;
                await _forceLogout();
            } else {
                toast(defaultErrorToast((e as Error).message)) ;
            }
        }
    }

    const onSearchCorporateUser = async (search?: Nullable<string>): Promise<void> => {
        userToShowRef.current = null;
        setVisibleUserInfos(false) ;
        setWorkedCorporateUsers($ok(search) ? users!.filter(user => user.lastName.includes(search!.toUpperCase()) || user.email === search?.toLowerCase()) : users) ;
    }

    const onClickViewUser = async (userId: UUID) => {
        try {
            const userParameters = await _toUserParameters(userId) ;
            userToShowRef.current = userParameters;
            setVisibleUserInfos(true) ;
            _setUserButtons(userParameters) ;
            setIsCreation(false) ;

            const currentConsumptions = await getCurrentConsumption(userId);
            setNbQualified($ok(currentConsumptions) ? currentConsumptions!.qualified : -1) ;
            setNbAdvanced($ok(currentConsumptions) ? currentConsumptions!.advanced : -1) ;
            setConsumptions(await getConsumptions(userId)) ;
        } catch (e) {
            if ((e as Error).message === Resp.Unauthorized.toString()) {
                toast(defaultErrorToast(`Erreur lors de la récupération des données ${isConnectedAdminCorporate ? "du collaborateur" : "de l'utilisateur"}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`)) ;
                await _forceLogout();
            } else {
                toast(defaultErrorToast((e as Error).message)) ;
            }
        }
    }

    const onClickAddUser = () => {
        userToShowRef.current = isConnectedAdminCorporate ? { lastName: "", firstName: "", email: $email("")!, corporationId: connectedUser?.corporations?.first()?.apid } : null;
        setVisibleUserInfos(true) ;
        setIsCreation(true) ;
        setIsAddedAdminCorporate(false) ;
        _setUserButtons() ;
        setIsCorporateUserToShow(isConnectedAdminCorporate) ;
        if (searchParams.get('view') !== null || searchParams.get("corporationId") !== null) { // Remove url param if exists when click on add user button
            const url = new URL(window.location.href) ;
            url.searchParams.delete("corporationId") ;
            url.searchParams.delete("view") ;
            window.history.replaceState({}, '', url.toString()) ;
        } 
    }

    const onSubmitDeleteUser = async (data: UserParameters) => {
        if (userToShowRef.current?.apid !== data.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à supprimer n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir supprimer définitivement ${data.firstName} ${data.lastName} ?`,
            onAccept: async () => {
                try {
                    const success = await deleteUser(data.apid!);
                    if (success) {
                        const actionMsg = !data.userFlags?.activated || data.userFlags?.profile === UserProfile.Administrator || data.userFlags?.profile === UserProfile.User ? 'supprimé' : 'retiré des effectifs';
                        toast(defaultSuccessToast(`Le compte ${data.userFlags?.activated ? "" : "non activé"} de ${data.firstName} ${data.lastName} a été ${actionMsg}`));
                        if (isConnectedAdminCorporate) { // Only reload corporate users when view show collaborators (in case of Administrator Corporate connected)
                            _reloadCorporateUsers();
                        } else {
                            setUsers(prev => {
                                const index = prev.findIndex(u => u.apid === data.apid);
                                if (index === -1) { return prev };

                                const newusers = [...prev];
                                newusers.splice(index, 1);
                                return newusers;
                            });
                        }
                        userToShowRef.current = null;
                        setVisibleUserInfos(false);
                    } else {
                        toast(defaultInfoToast(`Erreur lors de la suppression définitive de ${data.firstName} ${data.lastName}`));
                    }
                } catch (e) {
                    if (e instanceof TSError) {
                        if (e.errorCode === Resp.Unauthorized) {
                            toast(defaultErrorToast(`Erreur lors de la suppression définitive de ${data.firstName} ${data.lastName}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`));
                            await _forceLogout();
                        } else if (e.errorCode === Resp.Conflict) {
                            toast(defaultErrorToast(`L'utilisateur ${data.firstName} ${data.lastName} ne peut pas être supprimé de la plateforme, car il est défini comme contact d'autres utilisateurs et a potentiellement déjà signé des documents.`));
                        } else {
                            toast(defaultErrorToast(`Impossible de supprimer définitivement ${data.firstName} ${data.lastName}`));
                        }
                    } else {
                        toast(defaultErrorToast(`Impossible de supprimer définitivement ${data.firstName} ${data.lastName}`));
                    }
                }
                setConfirmModal(DefaultAsyncConfirmModalState);
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState);
            }
        });
    }

    const onSubmitUpdateUser = async (data: UserParameters): Promise<void> => {
        const success = await _patchUser(data);
        if (success) {
            await _reloadUser(data.apid!);
        }
    }

    const onSubmitSendActivationLink = async (data: UserParameters): Promise<void> => {
        if (userToShowRef.current?.apid !== data.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous de vouloir ${userToShowRef.current?.userFlags?.profile === UserProfile.Contact ? "activer l'accès de" : "renvoyer le lien d'activation à"} ${data.firstName} ${data.lastName} ?`,
            onAccept: async () => {
                try {
                    let success = true;
                    if (userToShowRef.current?.userFlags?.profile === UserProfile.Contact) {
                        success = await _patchUser(data);
                    }
                    if (!success) { return; }

                    if (await createAuthentication(data.apid!, $password(12, { usesDigits: true, usesLowercase: true, usesUppercase: true, usesSpecials: true }))) {
                        toast(defaultSuccessToast(`Lien d'activation pour ${data.firstName} ${data.lastName} renvoyé à l'email ${data.email.toString()}`)) ;
                        await _reloadUser(data.apid!) ;
                    } else {
                        toast(defaultInfoToast(`Erreur lors du renvoi du lien d'activation`)) ;
                    }
                } catch (e) {
                    if ((e as Error).message === Resp.Unauthorized.toString()) {
                        toast(defaultErrorToast("Erreur lors du renvoi du lien d'activation. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                        await _forceLogout();
                    } else {
                        toast(defaultErrorToast(`Impossible de renvoyer un lien d'activation à ${data.firstName} ${data.lastName}`)) ;
                    }
                }
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    } ;

    const onSubmitAddUser = async (data: UserParameters) => {
        try {
            const userDto: UserDto = {
                firstName: data.firstName.capitalize(),
                lastName: data.lastName.toUpperCase(),
                email: data.email
            } ;
            if ($length(data.organizationName) !== 0) {
                userDto.organizationName = data.organizationName?.trim().toUpperCase() ;
            }
            if ($length(data.vatNumber) !== 0) {
                userDto.vatNumber = data.vatNumber?.trim().toUpperCase() ;
            }
            if ($ok(data.organizationAddress)) {
                userDto.organizationAddress = data.organizationAddress ;
            }

            let user: UserDto ;
            if (isCorporateUserToShow) {
                user = isAddedAdminCorporate ? await createAdminCorporate(data.corporationId!, userDto) : await createCorporationUser(connectedUser?.corporations?.first()?.apid || data.corporationId!, userDto) ;
            } else {
                user = await createUser(userDto) ;
            }

            const userId = user.apid! ;
            const success = await createAuthentication(userId!, $password(12, { usesDigits: true, usesLowercase: true, usesUppercase: true, usesSpecials: true })) ;
            if (success) {
                userToShowRef.current = isConnectedAdminCorporate ? { lastName: "", firstName: "", email: $email("")!, corporationId: connectedUser?.corporations?.first()?.apid } : null;
                toast(defaultSuccessToast(`${isAddedAdminCorporate ? "Gestionnaire" : "Utilisateur"} ${userDto.firstName} ${userDto.lastName} créé`)) ;
                if (isConnectedAdminCorporate) { // Only reload corporate users when view show collaborators (in case of Administrator Corporate connected)
                    await _reloadCorporateUsers() ;
                } else if (isAddedAdminCorporate) {
                    navigate(`/admin/corporations?corporationId=${data.corporationId}`) ;
                }
            } else {
                toast(defaultWarningToast(`Erreur lors de l'ajout des informations d'authentification`)) ;
            }
        } catch (e) {
            if (e instanceof TSError) {
                if (e.errorCode === Resp.Unauthorized) {
                    toast(defaultErrorToast(`Erreur lors de l'ajout d'un ${isConnectedAdminCorporate ? "collaborateur" : "utilisateur"}. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.`)) ;
                    await _forceLogout();
                } else if (e.errorCode === Resp.Conflict) {
                    toast(defaultErrorToast(`Vous ne pouvez pas ajouter un nouvel utilisateur à votre organisation parce que vous avez atteint la limite du nombre de collaborateurs autorisés`)) ;
                } else {
                    toast(defaultErrorToast(e.message));
                }
            } else {
                toast(defaultErrorToast((e as Error).message)) ;
            }
        }
    } ;

    const onSubmitAddAdminRole = async (data: UserParameters) => {
        if (userToShowRef.current?.apid !== data.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        const oldRole = data.userFlags?.profile ;
        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir ajouter le rôle ${oldRole === UserProfile.User ? "d'Administrateur" : "de Gestionnaire"} à ${data.firstName} ${data.lastName} ?`,
            onAccept: async () => {
                if (await setAdminRole(data.apid!)) {
                    toast(defaultSuccessToast(`Une notification a été envoyée à ${data.firstName} ${data.lastName} afin de le prévenir de son nouveau rôle ${oldRole === UserProfile.User ? "d'Administrateur" : "de Gestionnaire"}`)) ;
                    await _reloadUser(data.apid!) ;
                } else {
                    toast(defaultErrorToast(`Impossible d'ajouter le rôle ${oldRole === UserProfile.User ? "d'Administrateur" : "de Gestionnaire"} à ${data.firstName} ${data.lastName}`)) ;
                }
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    } ;

    const onSubmitDeleteAdminRole = async (data: UserParameters) => {
        if (userToShowRef.current?.apid !== data.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return;
        }

        const oldRole = data.userFlags?.profile ;
        setConfirmModal({
            title: "Confirmation",
            message: `Etes-vous sûr de vouloir supprimer le rôle ${oldRole === UserProfile.Administrator ? "d'Administrateur" : "de Gestionnaire"} à ${data.firstName} ${data.lastName} ?`,
            onAccept: async () => {
                if (await setAdminRole(data.apid!, false)) {
                    toast(defaultSuccessToast(`Une notification a été envoyée à ${data.firstName} ${data.lastName} afin de le prevénir qu'il n'est plus ${oldRole === UserProfile.Administrator ? "Administrateur" : "Gestionnaire"}`)) ;
                    await _reloadUser(data.apid!) ;
                } else {
                    toast(defaultErrorToast(`Impossible de supprimer le rôle ${oldRole === UserProfile.Administrator ? "d'Administrateur" : "de Gestionnaire"} à ${data.firstName} ${data.lastName}`)) ;
                }
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            },
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
            }
        }) ;
    } ;

    const onSubmitLinkToCorporate = async (data: UserParameters) => {
        setIsOpenLinkUserModal(true);
        setUsernameToLink(`${data.firstName.capitalize()} ${data.lastName.toUpperCase()}`);
        setUserIdToLink(data.apid);
    }

    const onExportUserConsumptions = async () => {
        if ($ok(userToShowRef.current)) {
            try {
                const username = `${userToShowRef.current?.firstName.capitalize()}_${userToShowRef.current?.lastName.toUpperCase()}` ;
                await exportConsumptions(userToShowRef.current?.apid!, username) ;
            } catch (e) {
                if ((e as Error).message === Resp.Unauthorized.toString()) {
                    toast(defaultErrorToast("Erreur lors de l'export des données. Soit vous avez dépassé le délai d'inactivité sur l'interface, soit vous n'êtes pas autorisé à y accéder. Vous avez été automatiquement déconnecté, veuillez-vous authentifier à nouveau.")) ;
                    await _forceLogout();
                } else {
                    toast(defaultErrorToast("Problème rencontré lors de l'export des données")) ;
                }
            }
        }
    } ;

    const onLinkUserToCorporate = async (corporation: Nullable<CorporationDto>) => { 
        setIsOpenLinkUserModal(false) ;
        if ($ok(corporation)) {
            setConfirmModal({
                title: "Confirmation",
                message: `Etes-vous sûr de vouloir rattacher ${usernameToLink} à l'organisation '${corporation?.name}' ?`,
                onAccept: async () => {
                    setConfirmModal(DefaultAsyncConfirmModalState) ;
                    onOpen() ;
                    try {
                        const newUser = await linkUserToCorporation(corporation!.apid!, userIdToLink!) ;
                        if ($ok(newUser)) {
                            const index  = newUser.email.lastIndexOf(".") ;
                            newUser.email = $email(newUser.email.substring(0, index))! ;
                            if (await patchUser(newUser) !== null) {
                                toast(defaultInfoToast(`Une notification a été envoyée à l'utilisateur ${usernameToLink} afin de lui indiquer son rattachement à l'organisation '${corporation?.name}'`)) ;
                                onSearchUser(null) ;
                            } else {
                                toast(defaultErrorToast(`Impossible de rattacher l'utilisateur ${usernameToLink} à l'organisation '${corporation?.name}'`)) ;
                            }
                        } else {
                            toast(defaultErrorToast(`Impossible de rattacher l'utilisateur ${usernameToLink} à l'organisation '${corporation?.name}'`)) ;
                        }
                    } catch (e) {
                        if ((e as Error).message === Resp.Conflict.toString()) {
                            toast(defaultErrorToast(`Vous ne pouvez pas rattacher l'utilisateur ${usernameToLink} parce que la limite du nombre de collaborateurs pour l'organisation '${corporation?.name}' est atteinte`)) ;
                        } else {
                            toast(defaultErrorToast(`Impossible de rattacher l'utilisateur ${usernameToLink} à l'organisation '${corporation?.name}'`)) ;
                        }
                    }
                    onClose() ;
                },
                onClose: async () => {
                    setConfirmModal(DefaultAsyncConfirmModalState) ;
                }
            })
        }
    } ;

    const _renderCreateUserInfosPanel = () => {
        return isCorporateUserToShow ? <CorporationUserInfos maxWidth={maxWidth} visibility={visibleUserInfos ? "visible" : "hidden"} isAdminCorporate={isConnectedAdminCorporate} isCreation={isCreation} data={userToShowRef.current} actionButtons={actionButtons} onBack={() => setVisibleUserInfos(false)}  /> 
            : <UserInfos maxWidth={maxWidth} visibility={visibleUserInfos ? "visible" : "hidden"} data={userToShowRef.current} countries={countries} isCreation={isCreation} actionButtons={actionButtons} onBack={() => setVisibleUserInfos(false)}  />
    }

    const _setUserButtons = (user?: Nullable<UserParameters>) => {
        const buttons: ActionButton<UserParameters>[] = [] ;
        const userFlags = user?.userFlags ;
        if (!$ok(userFlags)) {
            buttons.push({ title: "Créer", variant: "important", onSubmit: onSubmitAddUser }) ;
        } else if (!userFlags?.activated) {
            buttons.push({ title: userFlags?.profile === UserProfile.Contact ? "Activer l'accès" : "Renvoyer le lien d'activation", variant: "important", onSubmit: onSubmitSendActivationLink }) ;
            buttons.push({ title: "Supprimer", variant: "important", onSubmit: onSubmitDeleteUser }) ;
        } else if (connectedUser?.apid !== user?.apid) { // Only actions for other users
            switch (userFlags.profile) {
                case UserProfile.User:
                    if (connectedUser?.userFlags?.profile === UserProfile.Root) {
                        buttons.push({ title: "Définir comme Administrateur", variant: "important", onSubmit: onSubmitAddAdminRole }) ;
                    }
                    buttons.push({ title: "Mettre à jour", variant: "important", disabled: _isCompleteUserData(user), onSubmit: onSubmitUpdateUser });
                    buttons.push({ title: "Supprimer", variant: "important", onSubmit: onSubmitDeleteUser }) ;
                    if (connectedUser?.userFlags?.profile === UserProfile.Root) {
                        buttons.push({ title: "Rattacher à une organisation", variant: "optional", onSubmit: onSubmitLinkToCorporate }) ;
                    }
                    break ;

                case UserProfile.Administrator :
                    if (connectedUser?.userFlags?.profile === UserProfile.Root) {
                        buttons.push({ title: "Supprimer le rôle Administrateur", variant: "important", onSubmit: onSubmitDeleteAdminRole }) ;
                        buttons.push({ title: "Supprimer", variant: "important", onSubmit: onSubmitDeleteUser }) ;
                    }
                    buttons.push({ title: "Mettre à jour", variant: "important", disabled: _isCompleteUserData(user), onSubmit: onSubmitUpdateUser });
                    buttons.push({ title: "Rattacher à une organisation", variant: "optional", onSubmit: onSubmitLinkToCorporate }) ;
                    break ;

                case UserProfile.UserCorporate:
                    buttons.push({ title: "Définir comme Gestionnaire", variant: "important", onSubmit: onSubmitAddAdminRole }) ;
                    buttons.push({ title: "Retirer des effectifs", variant: "important", onSubmit: onSubmitDeleteUser }) ;
                    break ;
                
                case UserProfile.AdministratorCorporate :
                    buttons.push({ title: "Supprimer le rôle Gestionnaire", variant: "important", onSubmit: onSubmitDeleteAdminRole }) ;
                    buttons.push({ title: "Retirer des effectifs", variant: "important", onSubmit: onSubmitDeleteUser }) ;
                    break ;

                default:
                    break ;
            }
        }
        setActionButtons(buttons) ;
    } ;

    const _reloadUser = async (userId: UUID) => {
        const userParameters = await _toUserParameters(userId);
        userToShowRef.current = userParameters;
        _setUserButtons(userParameters);
        const index = users.findIndex(user => user.apid === userId);
        if (index !== -1) {
            if (isCorporateUserToShow) {
                _reloadCorporateUsers();
            } else {
                const newusers = [...users];
                newusers.splice(index, 1, userParameters);
                setUsers(newusers);
            }
        }
    }

    const _reloadCorporateUsers = async () => {
        const users: UserDto[] = [] ;
        for (const corporation of connectedUser?.corporations!) {
            const userList = await getCorporationUsers(corporation.apid) ;
            if ($ok(userList)) { users.push(...userList!) ; }
        }
        setUsers(users) ;
        setWorkedCorporateUsers(users) ;
    }

    const _patchUser = async (data: UserParameters): Promise<boolean> => {
        if (userToShowRef.current?.apid !== data.apid) {
            toast(defaultErrorToast("L'identifiant de l'utilisateur à modifier n'est pas identique à l'identifiant de l'utilisateur sélectionné"));
            return false;
        }

        if ((userToShowRef.current?.firstName.trim().capitalize() !== data.firstName.trim().capitalize()) ||
            (userToShowRef.current.lastName.trim().toUpperCase() !== data.lastName.toUpperCase()) ||
            (userToShowRef.current.email.trim().toLowerCase() !== data.email.trim().toLowerCase())) {
            toast(defaultErrorToast("Les prénom, nom et email d'une personne ne peuvent pas être modifiés"));
            return false;
        }

        if ($ok(userToShowRef.current.organizationName) && $ok(userToShowRef.current.vatNumber) && $ok(userToShowRef.current.organizationAddress?.city)) {
            toast(defaultErrorToast("Le nom de l'entreprise, le numéro de TVA intracommunautaire et la ville sont déjà définis, vous ne pouvez pas les modifier"));
            return false;
        }

        const updatingUserDto: UserDto = {
            apid: data.apid,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
        };
        if (!$ok(userToShowRef.current.organizationName) && $length(data.organizationName?.trim()) > 0) {
            updatingUserDto.organizationName = data.organizationName?.trim().toUpperCase();
        }
        if (!$ok(userToShowRef.current.vatNumber) && $length(data.vatNumber?.trim()) > 0) {
            updatingUserDto.vatNumber = data.vatNumber?.trim();
        }
        if (!$ok(userToShowRef.current.organizationAddress?.city) && $length(data.organizationAddress?.city.trim()) > 0) {
            updatingUserDto.organizationAddress = data.organizationAddress;
        }

        try {
            await patchUser(updatingUserDto);
            toast(defaultSuccessToast(`Les données de ${data.firstName} ${data.lastName} ont été mises à jour`)) ;
            return true;
        } catch (e) {
            toast(defaultErrorToast(`Problème rencontré lors de la mise à jour des données de ${data.name}`));
            return false;
        }
    }

    const _forceLogout = async () => {
        disconnect();
        await logout();
        navigate(config.admin.loginURL!);
    }


    useEffect(() => {
        _setUserButtons() ;
    }, [isCorporateUserToShow, isAddedAdminCorporate]) ;

    useEffect(() => {
        const getCorporateUsers = async () => {
            await _reloadCorporateUsers() ;
        } ;
    
        const filteredCorpToLink = async () => {
            if ($ok(corporations)) {
                const filteredCorp = await Promise.all(corporations!.map(async corp => {
                    const users = await getCorporationUsers(corp!.apid);
                    if ($ok(corp.maxUsers) && $count(users) < corp.maxUsers!) { return corp; };    
                }));
                setAvailableCorpToLink(filteredCorp.filter(corp => $ok(corp)) as CorporationDto[]);
            }
        }

        if ($ok(connectedUser)) {
            if (searchParams.get('view') === '1' && searchParams.get('corporationId') !== null) {
                const corporationId = $UUID(searchParams.get('corporationId')) ;
                userToShowRef.current = {lastName: "", firstName: "", email: $email("")!, corporationId: $ok(corporationId) ? corporationId! : undefined };
                setVisibleUserInfos(true) ;
                setIsCreation(true) ;
                setIsCorporateUserToShow(true) ;
                setIsAddedAdminCorporate(true) ;
            } else if (connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate) {
                setIsConnectedAdminCorporate(true);
                getCorporateUsers() ;
                setIsCorporateUserToShow(true) ;
            } else {
                filteredCorpToLink();
            }
        }
    }, [connectedUser]) ;

    return (
        <PageShellWithThreeColumns
            centralContent={
                <>
                    { isCreation && isCorporateUserToShow && connectedUser?.userFlags?.profile !== UserProfile.AdministratorCorporate  ? <></> : // Replacing default view (search and user creation button) by admin corporate creation form
                        <ColumnTitle color={$g1Color('global.write')}>
                            <Text>Gestion des {isConnectedAdminCorporate ? "collaborateurs" : "utilisateurs"}</Text>
                        </ColumnTitle>
                    }
                    { isCreation && isCorporateUserToShow && connectedUser?.userFlags?.profile !== UserProfile.AdministratorCorporate  ? _renderCreateUserInfosPanel() : // Replacing default view (search and user creation button) by admin corporate creation form
                        <>
                            <Box mb="50">
                                <SearchUsers key={forceReload} maxWidth={maxWidth} displayIcon displayOrganizationName displayRole
                                             isAdminCorporate={isConnectedAdminCorporate}
                                             users={isConnectedAdminCorporate ? workedCorporateUsers : users}
                                             onSearch={isConnectedAdminCorporate ? onSearchCorporateUser : onSearchUser}
                                             onItemClick={onClickViewUser} />
                            </Box>
                            {/* {isConnectedAdminCorporate && (
                                <HStack justifyContent="center">
                                    <G1Button component="form" variant="important" title="Créer un collaborateur" marginRight="2%" fontSize="md" height="60px"
                                              isDisabled={$ok(displayReachedLimitOfUsers)} onClick={onClickAddUser} />
                                </HStack>
                            )} */}
                        </>
                    }
                </>
            }
            rightContent={
                <>
                { isCreation ? 
                    (isCorporateUserToShow && connectedUser?.userFlags?.profile !== UserProfile.AdministratorCorporate  ? <></> : _renderCreateUserInfosPanel()) // Admin corporate creation form moved to central view, so no form must be shown here
                    : <Tabs isFitted marginTop="10" visibility={visibleUserInfos ? "visible" : "hidden"}>
                        <TabList color={$g1Color('global.tabs.write')} backgroundColor={$g1Color('global.tabs.bg')}>
                            <Tab fontWeight="semibold">Compte</Tab>
                            <Tab fontWeight="semibold">Consommations</Tab>
                        </TabList>
                        <TabIndicator color={$g1Color('global.tabs.indicator.write')} backgroundColor={$g1Color('global.tabs.indicator.bg')} />
                        <TabPanels>
                            <TabPanel>
                                {isCorporateUserToShow  ? 
                                    <CorporationUserInfos maxWidth={maxWidth} isAdminCorporate={true} isCreation={isCreation} data={userToShowRef.current} actionButtons={actionButtons} onBack={() => setVisibleUserInfos(false)}  />
                                    : <UserInfos maxWidth={maxWidth} data={userToShowRef.current} countries={countries} actionButtons={actionButtons} onBack={() => setVisibleUserInfos(false)}  />
                                }
                            </TabPanel>
                            <TabPanel>
                                <ConsumptionsBox nbAdvanced={nbAdvanced} nbQualified={nbQualified} consumptions={consumptions} onExport={onExportUserConsumptions} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                }
                <WaitingModal isOpen={isOpen} onClose={onClose} message="Rattachement en cours. Veuillez patienter..." />
                <AsyncLinkUserModal isOpen={isOpenLinkUserModal} items={availableCorpToLink} onClose={async () => { setIsOpenLinkUserModal(false) ; }} onAccept={onLinkUserToCorporate} />
                <AsyncConfirmModal title={confirmModal.title} message={confirmModal.message} onAccept={confirmModal.onAccept} onClose={confirmModal.onClose} />
                </>    
            }
            isOpen={visibleUserInfos}
        />
    );
} ;

async function _toUserParameters(userId: UUID): Promise<UserParameters> {
    const userDto = await getUserProfile(userId);
    const corporation = userDto.corporations?.first();
    return {
        lastName: userDto.lastName.toUpperCase(),
        firstName: userDto.firstName.capitalize(),
        email: userDto.email,
        organizationName: $ok(corporation) ? corporation?.name : userDto.organizationName,
        organizationAddress: $ok(corporation) ? corporation?.address : userDto.organizationAddress,
        vatNumber: $ok(corporation) ? "" : userDto.vatNumber,
        apid: userDto.apid,
        createdAt: userDto.createdAt,
        userFlags: userDto.userFlags,
        corporationId: corporation?.apid
    };
}

const _isCompleteUserData = (user?: Nullable<UserParameters>): boolean => {
    return $length(user?.firstName.trim()) > 0 && $length(user?.lastName.trim()) > 0 && $length(user?.email.trim()) > 0 && $length(user?.organizationName?.trim()) > 0 &&
           $length(user?.vatNumber?.trim()) > 0 && $length(user?.organizationAddress?.city.trim()) > 0;
}